import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apacuanaWeb } from 'apacuana-sdk-web';
import { sdkConfigNaturalPerson, sdkConfigProfessional } from 'screens/apacuana/utils';
import { ApacuanaService } from 'services';

const ApacuanaContext = createContext(null);

// Mientras la pestaña esté abierta se re-sincroniza el token con el backend: lo
// devuelve igual si sigue fresco, o uno nuevo si entró en su ventana de renovación
// (Apacuana lo vence cada 24h). Barato: el backend lo sirve desde BD.
const TOKEN_SYNC_INTERVAL_MS = 30 * 60 * 1000;

///NOTA: para mas informacion sobre el apacuana-sdk-web: https://www.npmjs.com/package/apacuana-sdk-web

export const ApacuanaProvider = ({ children, user }) => {
  const [sdk, setSdk] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [customerId, setCustomerId] = useState(
    user && user.apacuanaId ? user.apacuanaId : undefined,
  );
  // Último token entregado por el backend (el "secretKey" del SDK).
  const secretKeyRef = useRef('');
  const customerIdRef = useRef(customerId);
  customerIdRef.current = customerId;

  const currentConfig =
    user?.level?.id === 4
      ? { ...sdkConfigProfessional, customerId }
      : { ...sdkConfigNaturalPerson, customerId };

  ///Configuracion correspondiente al ambiente de pruebas de legalmente en apacuana
  const sdkConfig = currentConfig;

  // El secretKey ya no vive en el front: lo renueva y persiste el backend.
  const fetchSecretKey = useCallback(async ({ refresh = false } = {}) => {
    const res = refresh
      ? await ApacuanaService.refreshToken({
          current_token: secretKeyRef.current || undefined,
        })
      : await ApacuanaService.getToken();
    if (!res?.token) {
      throw new Error('El backend no devolvió el token de Apacuana.');
    }
    secretKeyRef.current = res.token;
    return res.token;
  }, []);

  const initSdk = useCallback(
    async (id, knownSecretKey) => {
      try {
        const secretKey = knownSecretKey || (await fetchSecretKey());
        const baseConfig = id ? { ...sdkConfig, customerId: id } : sdkConfig;
        const configToInit = { ...baseConfig, secretKey };

        await apacuanaWeb.init(configToInit);
        setSdk(() => apacuanaWeb);
        setIsInitialized(true);
        console.log(
          `Apacuana SDK Provider: Initialized successfully. with ID: ${
            configToInit.customerId ? 'yes' : 'no'
          }`,
        );
      } catch (err) {
        setError(err);
        console.error('Apacuana SDK Provider: Initialization failed.', err);
      }
    },
    [fetchSecretKey],
  );

  // Pide el token al backend y, si cambió respecto al que usa el SDK, lo
  // re-inicializa con el nuevo. `force` = Apacuana rechazó el token: el backend lo
  // valida y, solo si de verdad no sirve, genera uno nuevo (con lock, un solo login).
  const syncSecretKey = useCallback(
    async ({ force = false } = {}) => {
      try {
        const previous = secretKeyRef.current;
        const next = await fetchSecretKey({ refresh: force });
        if (previous && next !== previous) {
          await initSdk(customerIdRef.current, next);
        }
      } catch (err) {
        console.error('Apacuana SDK Provider: no se pudo renovar el token.', err);
      }
    },
    [fetchSecretKey, initSdk],
  );

  const updateSdkConfig = useCallback(async (newCustomerId, saveInLocal = true) => {
    if (saveInLocal) {
      localStorage.setItem('apacuana_customer_id', newCustomerId);
    }
    setCustomerId(newCustomerId);

    // Forzar la reinicialización del SDK con el nuevo ID
    await initSdk(newCustomerId);
  }, []);

  const initApacuanaSdk = (customerId) => {
    if (!isInitialized) {
      initSdk(customerId);
    }
  };

  useEffect(() => {
    // El token del backend requiere sesión: sin usuario no hay nada que iniciar.
    if (!user?.id) return;
    initApacuanaSdk(customerId);
  }, [isInitialized, customerId, user]);

  // Mantener el token al día mientras la pestaña esté abierta (puede quedar horas
  // o días): al volver a la pestaña y cada 30 min.
  useEffect(() => {
    if (!user?.id) return undefined;

    const onVisible = () => {
      if (document.visibilityState === 'visible') syncSecretKey();
    };
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(() => syncSecretKey(), TOKEN_SYNC_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, [user?.id, syncSecretKey]);

  const value = {
    sdk,
    isInitialized,
    error,
    updateSdkConfig,
    initApacuanaSdk,
    setIsInitialized,
    // Llamar cuando una llamada del SDK falle por token vencido.
    refreshSecretKey: () => syncSecretKey({ force: true }),
    user: { ...user, apacuanaId: customerId },
  };

  return <ApacuanaContext.Provider value={value}>{children}</ApacuanaContext.Provider>;
};

export const useApacuana = () => {
  const context = useContext(ApacuanaContext);
  if (context === undefined) {
    throw new Error('useApacuana must be used within an ApacuanaProvider');
  }
  return context;
};
